<template>
  <div class="public-digital-agreement-page">
    <div class="agreement-container">
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">Loading agreement...</p>
      </div>

      <div v-else-if="error" class="text-center py-5">
        <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-2" />
        <h4 class="text-danger">{{ error }}</h4>
      </div>

      <div v-else-if="success" class="text-center py-5">
        <feather-icon icon="CheckCircleIcon" size="48" class="text-success mb-2" />
        <h4 class="text-success">{{ successMessage }}</h4>
        <p class="text-muted mt-2">You may close this window.</p>
      </div>

      <div v-else-if="agreement">
        <div class="agreement-header text-center mb-3">
          <h3 class="mb-1">Digital Agreement</h3>
          <p v-if="agreement.recipient_name" class="text-muted mb-0">
            For: {{ agreement.recipient_name }}
            <span v-if="agreement.recipient_email">({{ agreement.recipient_email }})</span>
          </p>
        </div>

        <b-card class="agreement-content-card mb-3">
          <div class="agreement-html-content" v-html="agreement.agreement_html" />
        </b-card>

        <div v-if="agreement.status === 'signed' || agreement.status === 'rejected'" class="text-center py-4">
          <b-badge :variant="agreement.status === 'signed' ? 'success' : 'danger'" class="p-2">
            This agreement has already been {{ agreement.status }}.
          </b-badge>
        </div>

        <div v-else>
          <b-card v-if="activeFlow === 'sign'" class="mb-3">
            <h5 class="mb-2">Sign Agreement</h5>
            <digital-agreement-signature-pad ref="signaturePad" />
            <b-form-checkbox v-model="agreed" class="mt-2">
              I have read and agree to the terms stated in this agreement.
            </b-form-checkbox>
            <div class="d-flex flex-wrap gap-2 mt-3 justify-content-end">
              <b-button variant="outline-secondary" :disabled="submitting" @click="activeFlow = null">
                Cancel
              </b-button>
              <b-button variant="success" :disabled="!canSign || submitting" @click="handleSign">
                <b-spinner v-if="submitting" small class="mr-1" />
                Submit Signature
              </b-button>
            </div>
          </b-card>

          <b-card v-else-if="activeFlow === 'reject'" class="mb-3">
            <h5 class="mb-2">Reject Agreement</h5>
            <b-form-group label="Reason for rejection" label-for="reject-reason">
              <b-form-textarea
                id="reject-reason"
                v-model="rejectReason"
                rows="4"
                placeholder="Please provide a reason (minimum 5 characters)"
              />
              <small v-if="rejectReason.length > 0 && rejectReason.trim().length < 5" class="text-danger">
                Reason must be at least 5 characters.
              </small>
            </b-form-group>
            <div class="d-flex flex-wrap gap-2 mt-2 justify-content-end">
              <b-button variant="outline-secondary" :disabled="submitting" @click="activeFlow = null">
                Cancel
              </b-button>
              <b-button
                variant="danger"
                :disabled="rejectReason.trim().length < 5 || submitting"
                @click="handleReject"
              >
                <b-spinner v-if="submitting" small class="mr-1" />
                Confirm Rejection
              </b-button>
            </div>
          </b-card>

          <div v-else class="d-flex flex-wrap gap-2 justify-content-center">
            <b-button variant="success" size="lg" @click="activeFlow = 'sign'">
              Agree and Sign
            </b-button>
            <b-button variant="outline-danger" size="lg" @click="activeFlow = 'reject'">
              Reject
            </b-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BCard, BButton, BSpinner, BBadge, BFormCheckbox, BFormGroup, BFormTextarea } from 'bootstrap-vue'
import DigitalAgreementSignaturePad from '@/components/DigitalAgreementSignaturePad.vue'
import { fetchAgreementByToken, signAgreement, rejectAgreement } from '@/api/digitalAgreement'

export default {
  name: 'PublicDigitalAgreementSign',
  components: {
    BCard,
    BButton,
    BSpinner,
    BBadge,
    BFormCheckbox,
    BFormGroup,
    BFormTextarea,
    DigitalAgreementSignaturePad,
  },
  data() {
    return {
      loading: true,
      submitting: false,
      error: null,
      success: false,
      successMessage: '',
      agreement: null,
      activeFlow: null,
      agreed: false,
      rejectReason: '',
    }
  },
  computed: {
    token() {
      return this.$route.params.token
    },
    canSign() {
      return this.agreed && this.$refs.signaturePad && this.$refs.signaturePad.hasDrawn
    },
  },
  async mounted() {
    await this.loadAgreement()
  },
  methods: {
    async loadAgreement() {
      this.loading = true
      this.error = null
      try {
        const response = await fetchAgreementByToken(this.token)
        this.agreement = response.data?.data || response.data
      } catch (err) {
        this.error = err.response?.data?.message || 'Unable to load agreement. The link may be invalid or expired.'
      } finally {
        this.loading = false
      }
    },
    async handleSign() {
      const pad = this.$refs.signaturePad
      if (!this.agreed || !pad || !pad.hasDrawn) return
      const signature = pad.getSignatureDataUrl()
      if (!signature) return

      this.submitting = true
      try {
        await signAgreement(this.token, {
          signature,
          agreed: true,
        })
        this.success = true
        this.successMessage = 'Agreement signed successfully. Thank you!'
      } catch (err) {
        this.$bvToast.toast(err.response?.data?.message || 'Failed to sign agreement.', {
          title: 'Error',
          variant: 'danger',
        })
      } finally {
        this.submitting = false
      }
    },
    async handleReject() {
      if (this.rejectReason.trim().length < 5) return
      this.submitting = true
      try {
        await rejectAgreement(this.token, {
          rejection_reason: this.rejectReason.trim(),
        })
        this.success = true
        this.successMessage = 'Agreement rejected. Your response has been recorded.'
      } catch (err) {
        this.$bvToast.toast(err.response?.data?.message || 'Failed to reject agreement.', {
          title: 'Error',
          variant: 'danger',
        })
      } finally {
        this.submitting = false
      }
    },
  },
}
</script>

<style scoped>
.public-digital-agreement-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  padding: 1.5rem 1rem;
}

.agreement-container {
  max-width: 900px;
  margin: 0 auto;
}

.agreement-header h3 {
  color: #f8fafc;
}

.agreement-content-card {
  max-height: 50vh;
  overflow-y: auto;
}

.agreement-html-content {
  font-size: 0.95rem;
  line-height: 1.6;
}

.gap-2 {
  gap: 0.5rem;
}

@media (max-width: 576px) {
  .public-digital-agreement-page {
    padding: 1rem 0.75rem;
  }

  .agreement-content-card {
    max-height: 40vh;
  }
}
</style>
